class SihaFaq extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
    this.singleOpen = this.dataset.singleOpen !== 'false';
    this.duration = Math.max(Number.parseInt(this.dataset.duration, 10) || 300, 0);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handleClick = this.onClick.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleToggle = this.onToggle.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('toggle', this.handleToggle, true);

    this.items.forEach((item) => this.updateState(item));
    this.initialized = true;
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('toggle', this.handleToggle, true);
    this.items.forEach((item) => this.cancelAnimation(item));
    this.initialized = false;
  }

  onClick(event) {
    const summary = event.target.closest('summary');
    if (!summary || !this.contains(summary)) return;

    const item = summary.closest('[data-faq-item]');
    if (!item) return;

    event.preventDefault();
    const shouldOpen = !item.open || item.classList.contains('is-closing');

    if (shouldOpen) {
      this.openItem(item);
    } else {
      this.closeItem(item);
    }
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const openItem = event.target.closest('[data-faq-item][open]') || this.querySelector('[data-faq-item][open]');
    if (!openItem) return;

    this.closeItem(openItem);
    openItem.querySelector('summary')?.focus();
  }

  onToggle(event) {
    const item = event.target;
    if (!(item instanceof HTMLDetailsElement) || !item.matches('[data-faq-item]')) return;
    this.updateState(item);
  }

  updateState(item, forcedState = null) {
    const isOpen = forcedState ?? (item.open && !item.classList.contains('is-closing'));
    item.classList.toggle('is-open', isOpen);
    item.querySelector('summary')?.setAttribute('aria-expanded', String(isOpen));
  }

  openItem(item) {
    if (this.singleOpen) {
      this.items.forEach((otherItem) => {
        if (otherItem !== item && otherItem.open) this.closeItem(otherItem);
      });
    }

    const startHeight = item.getBoundingClientRect().height;
    this.cancelAnimation(item);
    item.open = true;
    item.classList.remove('is-closing');
    item.classList.add('is-opening');
    this.updateState(item, true);

    if (this.reduceMotion.matches || !item.animate) {
      this.finishAnimation(item, true);
      return;
    }

    const endHeight = item.scrollHeight;
    item.style.height = `${startHeight}px`;
    item.style.overflow = 'hidden';

    item.sihaAnimation = item.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
    item.sihaAnimation.onfinish = () => this.finishAnimation(item, true);
  }

  closeItem(item) {
    if (!item.open) return;

    const startHeight = item.getBoundingClientRect().height;
    const summary = item.querySelector('summary');
    const endHeight = summary?.getBoundingClientRect().height || 0;

    this.cancelAnimation(item);
    item.classList.remove('is-opening');
    item.classList.add('is-closing');
    this.updateState(item, false);

    if (this.reduceMotion.matches || !item.animate) {
      this.finishAnimation(item, false);
      return;
    }

    item.style.height = `${startHeight}px`;
    item.style.overflow = 'hidden';
    item.sihaAnimation = item.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
    item.sihaAnimation.onfinish = () => this.finishAnimation(item, false);
  }

  finishAnimation(item, shouldStayOpen) {
    item.open = shouldStayOpen;
    item.classList.remove('is-opening', 'is-closing');
    item.style.removeProperty('height');
    item.style.removeProperty('overflow');
    item.sihaAnimation = null;
    this.updateState(item, shouldStayOpen);
  }

  cancelAnimation(item) {
    if (item.sihaAnimation) {
      item.sihaAnimation.onfinish = null;
      item.sihaAnimation.cancel();
      item.sihaAnimation = null;
    }

    item.style.removeProperty('height');
    item.style.removeProperty('overflow');
  }
}

if (!customElements.get('siha-faq')) {
  customElements.define('siha-faq', SihaFaq);
}

